/**
 * The four speeds a player can be travelling at, and the conversions between them.
 *
 * Split out from quantize.ts because the offline converter's derived properties, the live
 * spatial state and the selector all need these and nothing else from that module.
 *
 * Sources: gamedefines.hpp:18-27, animcollection.hpp:57-104, utils.cpp GetVelocityID.
 */

/** gamedefines.hpp:18-21 — the four speeds, in metres per second. */
export const IDLE_VELOCITY = 0.0;
export const DRIBBLE_VELOCITY = 3.5;
export const WALK_VELOCITY = 5.0;
export const SPRINT_VELOCITY = 8.0;

/**
 * gamedefines.hpp:23 — the speed the sprint clips were actually ANIMATED at, which is not
 * the speed a sprinting player travels at.
 *
 * This gap is deliberate and load-bearing. A clip's own outgoing velocity snaps to 7.0
 * (animation.cpp:828), while the gameplay bucket for the same clip snaps to 8.0
 * (animcollection.hpp:61). The physics solver later stretches the authored 7.0 up to
 * whatever this particular player's top speed is, which is how two players with different
 * pace share one set of sprint animations. Do not reconcile the two numbers.
 */
export const ANIM_SPRINT_VELOCITY = 7.0;

/** gamedefines.hpp:25-27 — the thresholds between the buckets. */
export const IDLE_DRIBBLE_SWITCH = 1.8;
export const DRIBBLE_WALK_SWITCH = 4.2;
export const WALK_SPRINT_SWITCH = 6.0;

/** gamedefines.hpp:86-91. The numeric values are used directly as ids. */
export enum Velocity {
  Idle = 0,
  Dribble = 1,
  Walk = 2,
  Sprint = 3,
}

/** animcollection.hpp:57-63 — snap a speed DOWN to the bucket it falls in. */
export function rangeVelocity(velocity: number): number {
  if (velocity >= WALK_SPRINT_SWITCH) return SPRINT_VELOCITY;
  if (velocity >= DRIBBLE_WALK_SWITCH) return WALK_VELOCITY;
  if (velocity >= IDLE_DRIBBLE_SWITCH) return DRIBBLE_VELOCITY;
  return IDLE_VELOCITY;
}

/**
 * animcollection.hpp:71-77 — snap a speed UP to the next bucket at or above it.
 *
 * NOT the inverse of rangeVelocity and not a rounding of it: rangeVelocity(4.0) is 3.5
 * while floorVelocity(4.0) is 5.0. Despite the name it is a ceiling.
 */
export function floorVelocity(velocity: number): number {
  if (velocity <= 0) return IDLE_VELOCITY;
  if (velocity < DRIBBLE_VELOCITY) return DRIBBLE_VELOCITY;
  if (velocity <= WALK_VELOCITY) return WALK_VELOCITY;
  return SPRINT_VELOCITY;
}

/** animcollection.hpp:65-69 */
export function clampVelocity(velocity: number): number {
  if (velocity < 0) return 0;
  if (velocity > SPRINT_VELOCITY) return SPRINT_VELOCITY;
  return velocity;
}

/** animcollection.hpp:97-104 */
export function floatToEnumVelocity(velocity: number): Velocity {
  const ranged = rangeVelocity(velocity);
  if (ranged === IDLE_VELOCITY) return Velocity.Idle;
  if (ranged === DRIBBLE_VELOCITY) return Velocity.Dribble;
  if (ranged === WALK_VELOCITY) return Velocity.Walk;
  return Velocity.Sprint;
}

/** animcollection.hpp:79-95 */
export function enumToFloatVelocity(velocity: Velocity): number {
  switch (velocity) {
    case Velocity.Idle:
      return IDLE_VELOCITY;
    case Velocity.Dribble:
      return DRIBBLE_VELOCITY;
    case Velocity.Walk:
      return WALK_VELOCITY;
    case Velocity.Sprint:
      return SPRINT_VELOCITY;
  }
}

/**
 * utils.cpp — the bucket as a small integer, optionally collapsing dribble into walk.
 *
 * `treatDribbleAsWalk` is how the selector asks "is this roughly the same speed?" without
 * caring about the difference between jogging with the ball and walking.
 */
export function velocityID(velocity: Velocity, treatDribbleAsWalk = false): number {
  let id: number = velocity;
  if (treatDribbleAsWalk && id > 1) id--;
  return id;
}

/**
 * The snapping the OFFLINE converter applies to a clip's own incoming and outgoing speed
 * (animation.cpp:816-835, 893-912).
 *
 * Identical to rangeVelocity except that the sprint bucket lands on 7.0 rather than 8.0.
 * See the note on ANIM_SPRINT_VELOCITY. Kept as a separate function so neither can be
 * mistaken for the other at a call site.
 */
export function rangeAnimVelocity(velocity: number): number {
  if (velocity >= WALK_SPRINT_SWITCH) return ANIM_SPRINT_VELOCITY;
  if (velocity >= DRIBBLE_WALK_SWITCH) return WALK_VELOCITY;
  if (velocity >= IDLE_DRIBBLE_SWITCH) return DRIBBLE_VELOCITY;
  return IDLE_VELOCITY;
}
